import { TFunction } from 'i18next';

import dayjs from 'src/helpers/dayjs';

import { IBaseEdition, IAgePriceMatrix, EditionPricingPeriod } from 'src/interfaces/Edition';
import { IBaseParticipant } from 'src/interfaces/Participant';

type AgePriceMatrix = IAgePriceMatrix;

type EditionPricingSource = Partial<
  Omit<IBaseEdition, 'date' | 'signupDeadline' | 'earlyBirdEndsAt'>
> & {
  date?: Date | string;
  signupDeadline?: Date | string;
  earlyBirdEndsAt?: Date | string;
};

type NormalizedEditionPricing = {
  earlyBirdEndsAt: Date | null;
  signupDeadline: Date | null;
  year: number | undefined;
  childMaxAge: number;
  groupPrice?: number;
  pricing: {
    earlyBird: AgePriceMatrix;
    regular: AgePriceMatrix;
    atLocation: AgePriceMatrix;
  };
};

type CalculateRegistrationPriceOptions = {
  at?: Date;
  isAdminContext?: boolean;
  /** Force a period (e.g. regular for legacy display fallback). */
  period?: EditionPricingPeriod;
};

function pickAgePriceMatrix(
  source: Partial<AgePriceMatrix> | null | undefined,
): AgePriceMatrix {
  return {
    childPrice: Number(source?.childPrice),
    youngPrice: Number(source?.youngPrice),
    discountedPrice: Number(source?.discountedPrice),
    fullPrice: Number(source?.fullPrice),
  };
}

function isCompleteMatrix(source: Partial<AgePriceMatrix> | null | undefined): boolean {
  if (!source) {
    return false;
  }
  return (['childPrice', 'youngPrice', 'discountedPrice', 'fullPrice'] as const).every(key => {
    const value = source[key];
    return typeof value === 'number' && !Number.isNaN(value);
  });
}

export function normalizeEditionPricing(
  edition: EditionPricingSource | null | undefined,
): NormalizedEditionPricing {
  const flat = pickAgePriceMatrix(edition ?? undefined);
  const pricing = edition?.pricing;
  const childMaxAge =
    typeof edition?.childMaxAge === 'number' && !Number.isNaN(edition.childMaxAge)
      ? edition.childMaxAge
      : 6;
  const groupPrice =
    typeof edition?.groupPrice === 'number' && !Number.isNaN(edition.groupPrice)
      ? edition.groupPrice
      : undefined;

  return {
    earlyBirdEndsAt: edition?.earlyBirdEndsAt ? new Date(edition.earlyBirdEndsAt) : null,
    signupDeadline: edition?.signupDeadline ? new Date(edition.signupDeadline) : null,
    year: edition?.year,
    childMaxAge,
    groupPrice,
    pricing: {
      earlyBird: isCompleteMatrix(pricing?.earlyBird)
        ? pickAgePriceMatrix(pricing?.earlyBird)
        : { ...flat },
      regular: isCompleteMatrix(pricing?.regular)
        ? pickAgePriceMatrix(pricing?.regular)
        : { ...flat },
      atLocation: isCompleteMatrix(pricing?.atLocation)
        ? pickAgePriceMatrix(pricing?.atLocation)
        : { ...flat },
    },
  };
}

/** Age for pricing: prefer DOB as of edition event date, else year-only, else stored `age`. */
export function getParticipantAgeForEdition(
  participant: Pick<IBaseParticipant, 'dateOfBirth' | 'discounted'> & { age?: number },
  editionYear: number,
  editionDate?: Date | string | null,
): number | null {
  if (participant.dateOfBirth) {
    const dob = dayjs(participant.dateOfBirth);
    if (!dob.isValid()) {
      return null;
    }
    const eventDay =
      editionDate != null && editionDate !== '' ? dayjs(editionDate) : null;
    const age =
      eventDay?.isValid() ? eventDay.diff(dob, 'year') : editionYear - dob.year();
    return age >= 0 ? age : null;
  }
  if (typeof participant.age === 'number' && !Number.isNaN(participant.age)) {
    return participant.age;
  }
  return null;
}

/** Age for display/sort: prefer stored `age`, else DOB as of edition date (matches participant cards). */
export function getParticipantDisplayAge(
  participant: Pick<IBaseParticipant, 'dateOfBirth'> & { age?: number },
  editionYear?: number | null,
  editionDate?: Date | string | null,
): number | null {
  if (typeof participant.age === 'number' && !Number.isNaN(participant.age)) {
    return participant.age;
  }
  if (editionYear == null || !participant.dateOfBirth) {
    return null;
  }
  return getParticipantAgeForEdition(
    { dateOfBirth: participant.dateOfBirth, discounted: false },
    editionYear,
    editionDate,
  );
}

function priceFromMatrix(
  matrix: AgePriceMatrix,
  participant: Pick<IBaseParticipant, 'dateOfBirth' | 'discounted' | 'sportsGroupMember'> & {
    age?: number;
  },
  editionYear: number,
  childMaxAge = 6,
  editionDate?: Date | string | null,
): number {
  const age = getParticipantAgeForEdition(participant, editionYear, editionDate);
  const maxChildAge =
    typeof childMaxAge === 'number' && !Number.isNaN(childMaxAge) ? childMaxAge : 6;

  if (age !== null) {
    if (age <= maxChildAge) {
      return matrix.childPrice;
    }
    if (age <= 18) {
      return matrix.youngPrice;
    }
  }

  if (participant.discounted || participant.sportsGroupMember) {
    return matrix.discountedPrice;
  }

  return matrix.fullPrice;
}

type ResolvePricingPeriodResult =
  | { period: EditionPricingPeriod; matrix: AgePriceMatrix; errorCode?: undefined }
  | { errorCode: string; message: string; period?: undefined; matrix?: undefined };

function resolvePricingPeriod(
  edition: EditionPricingSource,
  options: CalculateRegistrationPriceOptions = {},
): ResolvePricingPeriodResult {
  const { at = new Date(), isAdminContext = false, period: forcedPeriod } = options;
  const normalized = normalizeEditionPricing(edition);

  if (forcedPeriod) {
    const matrix = normalized.pricing[forcedPeriod];
    return { period: forcedPeriod, matrix };
  }

  const atTime = at instanceof Date ? at.getTime() : new Date(at).getTime();
  const deadline = normalized.signupDeadline ? normalized.signupDeadline.getTime() : null;

  if (deadline !== null && atTime > deadline) {
    if (!isAdminContext) {
      return {
        errorCode: 'signupClosed',
        message: 'Signups are closed for this edition',
      };
    }
    return { period: 'atLocation', matrix: normalized.pricing.atLocation };
  }

  const earlyEnds = normalized.earlyBirdEndsAt ? normalized.earlyBirdEndsAt.getTime() : null;
  if (earlyEnds !== null && atTime <= earlyEnds) {
    return { period: 'earlyBird', matrix: normalized.pricing.earlyBird };
  }

  return { period: 'regular', matrix: normalized.pricing.regular };
}

export class RegistrationPriceError extends Error {
  errorCode: string;

  constructor(errorCode: string, message: string) {
    super(message);
    this.name = 'RegistrationPriceError';
    this.errorCode = errorCode;
  }
}

export function calculateRegistrationPrice(
  edition: EditionPricingSource,
  participant: Pick<IBaseParticipant, 'dateOfBirth' | 'discounted' | 'sportsGroupMember'> & {
    age?: number;
  },
  options: CalculateRegistrationPriceOptions = {},
): number {
  const resolved = resolvePricingPeriod(edition, options);
  if (resolved.errorCode || !resolved.matrix) {
    throw new RegistrationPriceError(
      resolved.errorCode ?? 'priceCalculationFailed',
      resolved.message ?? 'Could not calculate registration price',
    );
  }

  const normalized = normalizeEditionPricing(edition);
  const year = edition.year ?? normalized.year;
  if (year == null) {
    throw new RegistrationPriceError('missingEditionYear', 'Edition year is required for pricing');
  }
  return priceFromMatrix(
    resolved.matrix,
    participant,
    year,
    normalized.childMaxAge,
    edition.date,
  );
}

/**
 * Prefer the price saved on the registration (e.g. admin override, including 0 for free).
 * Falls back to calculated **regular** period when missing (null/undefined/NaN)
 * so display does not jump to today's at-location price.
 */
export function getRegistrationDisplayPrice(
  storedPrice: number | null | undefined,
  edition: EditionPricingSource | undefined,
  participant: Pick<IBaseParticipant, 'dateOfBirth' | 'discounted' | 'sportsGroupMember'> & {
    age?: number;
  },
): number | null {
  const hasSavedPrice =
    storedPrice != null && typeof storedPrice === 'number' && !Number.isNaN(storedPrice);

  if (hasSavedPrice) {
    return storedPrice;
  }
  if (!edition) {
    return null;
  }
  return calculateRegistrationPrice(edition, participant, { period: 'regular' });
}

/**
 * Pricing period for a registration at a given time (typically `createdAt`).
 * Always uses admin context so post-deadline dates resolve to `atLocation`
 * instead of throwing `signupClosed`.
 */
export function getRegistrationPricingPeriod(
  edition: EditionPricingSource | null | undefined,
  at: Date | string | null | undefined,
): EditionPricingPeriod | null {
  if (!edition || at == null || at === '') {
    return null;
  }
  const atDate = at instanceof Date ? at : new Date(at);
  if (Number.isNaN(atDate.getTime())) {
    return null;
  }
  const resolved = resolvePricingPeriod(edition, { at: atDate, isAdminContext: true });
  return resolved.period ?? null;
}

/** Composed "date · period" label for check-in and participant badge tooltips. */
export function formatRegistrationSignupInfo(
  t: TFunction,
  edition: EditionPricingSource | null | undefined,
  registeredAt: Date | string | null | undefined,
  dateFormat: string,
): string | null {
  if (registeredAt == null || registeredAt === '') {
    return null;
  }
  const at = dayjs(registeredAt);
  if (!at.isValid()) {
    return null;
  }
  const period = getRegistrationPricingPeriod(edition, at.toDate());
  const date = at.format(dateFormat);
  if (!period) {
    return date;
  }
  return t('registrations.signupInfo', {
    date,
    period: t(`editions.pricingPeriods.${period}`),
    // React already escapes; keep `/` in EN dateFormat (M/DD/YYYY) literal.
    interpolation: { escapeValue: false },
  });
}
